import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/api';
import { toast } from "@/components/ui/use-toast";
import { PageRenderer } from '@/components/pagebuilder/PageRenderer';
import { PageProvider } from '@/components/pagebuilder/PageContext.jsx';
import { EditableRenderer } from '@/components/pagebuilder/EditableRenderer.jsx';
import { BuilderPalette } from '@/components/pagebuilder/BuilderPalette.jsx';
import { TreeView } from '@/components/pagebuilder/TreeView.jsx';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, DragOverlay } from '@dnd-kit/core';
import NodeSettingsModal from '@/components/pagebuilder/NodeSettingsModal.jsx';
import { TEMPLATES, cloneTemplateWithNewIds, getLocalizedTemplates } from '@/components/pagebuilder/templates.js';

function fetchAuth(url, token, opts={}) {
  return fetch(url, { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, ...opts })
    .then(r => r.json());
}

export default function PagesAdmin({ forcedPageId, modeRestricted, hideTheme = false, hideTreeToggle = false, compactHeader = false, onMetaChange }){
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companySelf, setCompanySelf] = useState(null);
  const [themes, setThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // page object
  const [draftJson, setDraftJson] = useState('');
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [showRenderPreview, setShowRenderPreview] = useState(true);
  const [previewData, setPreviewData] = useState(null); // { page, theme, action, meta }
  const [builderTab, setBuilderTab] = useState('visual'); // visual | json
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [draftObj, setDraftObj] = useState(null);
  const [dirty, setDirty] = useState(false);
  const originalJsonRef = useRef('');
  const [autoRender, setAutoRender] = useState(true); // local live render always on but toggle reserved for future heavy components
  const [showVersionsSidebar, setShowVersionsSidebar] = useState(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const API = import.meta.env.VITE_API_BASE_URL || `${window.location.origin}/api`;
  
  // Undo/Redo System
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showImportExportModal, setShowImportExportModal] = useState(false);
  const importFileRef = useRef(null);
  const [clipboard, setClipboard] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [responsiveMode, setResponsiveMode] = useState('desktop'); // desktop | tablet | mobile
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  // Use a small delay to avoid accidental drags and reduce flicker from click/drag conflicts
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint:{ delay:120, tolerance:6 } }));
  const [activeDragId, setActiveDragId] = useState(null);
  const [inspectorTab, setInspectorTab] = useState('props'); // 'props' | 'style' | 'advanced'
  const [showPalette, setShowPalette] = useState(true);
  const [fullscreenEditor, setFullscreenEditor] = useState(false);
  const [inspectorView, setInspectorView] = useState('config'); // 'config' | 'navigator'
  
  // Resizable panels
  const [paletteWidth, setPaletteWidth] = useState(() => {
    try { return parseInt(localStorage.getItem('paletteWidth')) || 224; } catch { return 224; }
  });
  const [inspectorWidth, setInspectorWidth] = useState(() => {
    try { return parseInt(localStorage.getItem('inspectorWidth')) || 320; } catch { return 320; }
  });
  const [resizing, setResizing] = useState(null); // 'palette' | 'inspector' | null
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);

  // Escape Listener for fullscreen exit
  useEffect(()=>{
    if(!fullscreenEditor) return;
    function onKey(e){ if(e.key==='Escape'){ setFullscreenEditor(false); } }
    window.addEventListener('keydown', onKey);
    return ()=> window.removeEventListener('keydown', onKey);
  },[fullscreenEditor]);

  // Resize handlers
  useEffect(()=>{
    if(!resizing) return;
    function onMove(e){
      if(resizing === 'palette'){
        const delta = e.clientX - resizeStartX.current;
        const newWidth = Math.max(180, Math.min(600, resizeStartWidth.current + delta));
        setPaletteWidth(newWidth);
      } else if(resizing === 'inspector'){
        const delta = resizeStartX.current - e.clientX;
        const newWidth = Math.max(280, Math.min(800, resizeStartWidth.current + delta));
        setInspectorWidth(newWidth);
      }
    }
    function onUp(){
      setResizing(null);
      if(resizing === 'palette') localStorage.setItem('paletteWidth', paletteWidth);
      if(resizing === 'inspector') localStorage.setItem('inspectorWidth', inspectorWidth);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return ()=>{
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  },[resizing, paletteWidth, inspectorWidth]);

  useEffect(()=>{
    if(!token) return;
    setLoading(true);
    api.listPages(token).then(data => {
      if(Array.isArray(data)) setList(data);
      setLoading(false);
      let targetId = forcedPageId;
      if(!targetId){
        try {
          const params = new URLSearchParams(window.location.search);
          targetId = params.get('page');
        } catch {}
      }
      if(targetId){
        const found = data.find(p=>p.id===targetId);
        if(found) openPage(found);
      }
    }).catch(e=>{ setError(e.message); setLoading(false); });
    setThemesLoading(true);
    api.listThemes(token).then(t=>{ if(Array.isArray(t)) setThemes(t); setThemesLoading(false); }).catch(()=>setThemesLoading(false));
    // Fetch own company meta (for logo in editable preview)
    api.myCompany(token).then((c)=>{
      // Support wrapped ok or plain object
      if(c && c.ok && (c.id || c.data)){
        const normalized = c.data || { ...c };
        setCompanySelf({
          id: normalized.id,
          name: normalized.name,
          logoUrl: normalized.logoUrl || null,
          addressLine1: normalized.addressLine1 || null,
          addressLine2: normalized.addressLine2 || null,
          postalCode: normalized.postalCode || null,
          city: normalized.city || null,
          country: normalized.country || null,
        });
      } else if (c && (c.id || c.name)) {
        setCompanySelf({
          id: c.id,
          name: c.name,
          logoUrl: c.logoUrl || null,
          addressLine1: c.addressLine1 || null,
          addressLine2: c.addressLine2 || null,
          postalCode: c.postalCode || null,
          city: c.city || null,
          country: c.country || null,
        });
      }
    }).catch(()=>{/* non-fatal */});
  },[token, forcedPageId]);

  function openPage(p){
    setSelected(p);
    if(onMetaChange){
      try { onMetaChange({ title: p.title || '', slug: p.slug || '', dirty:false }); } catch {}
    }
    let raw = null;
    try { raw = p.draftJson || null; } catch { raw = null; }
    // Rekey tree if legacy nodes have missing/duplicate ids
    if(raw && raw.root){
      const { changed, tree } = rekeyTree(raw);
      if(changed){
        raw = tree;
        try { setDraftJson(JSON.stringify(raw, null, 2)); } catch { setDraftJson(''); }
        try { setDraftObj(raw); } catch { setDraftObj(null); }
        try { originalJsonRef.current = JSON.stringify(raw, null, 2); } catch { originalJsonRef.current=''; }
      } else {
        try { setDraftJson(JSON.stringify(raw, null, 2)); } catch { setDraftJson(''); }
        try { setDraftObj(raw); } catch { setDraftObj(null); }
        try { originalJsonRef.current = JSON.stringify(raw, null, 2); } catch { originalJsonRef.current=''; }
      }
    } else {
      try { setDraftJson(JSON.stringify(raw, null, 2)); } catch { setDraftJson(''); }
      try { setDraftObj(raw); } catch { setDraftObj(null); }
      try { originalJsonRef.current = JSON.stringify(raw, null, 2); } catch { originalJsonRef.current=''; }
    }
    // Auto-convert compact form into independent parts in register mode (no group required)
    try {
      if(modeRestricted==='register'){
        const hasCompact = containsType(raw?.root, 'codeRequestForm');
        if(hasCompact){
          const cur = JSON.parse(JSON.stringify(raw));
          const formNode = findFirstOfType(cur.root, 'codeRequestForm');
          const parent = findParent(cur.root, formNode.id);
          if(parent && Array.isArray(parent.children)){
            const idx = parent.children.findIndex(c=>c.id===formNode.id);
            const p = formNode.props || {};
            const parts = [
              { id: genId(), type:'codeRequestEmail', props:{ label: p.emailLabel || 'E-Mail' } },
              ...(p.showName ? [{ id: genId(), type:'codeRequestName', props:{ label: p.nameLabel || 'Name' } }] : []),
              ...(p.showConsent ? [{ id: genId(), type:'codeRequestConsent', props:{ label: p.consentLabel || 'Ich akzeptiere den Erhalt von Infos.', required: !!p.consentRequired } }] : []),
              { id: genId(), type:'codeRequestSubmit', props:{ submitLabel: p.submitLabel || 'Code anfordern', submitLabelLoading: p.submitLabelLoading || 'Sende...' } },
            ];
            parent.children.splice(idx, 1, ...parts);
            setDraftObj(cur);
            try { setDraftJson(JSON.stringify(cur, null, 2)); } catch {}
            originalJsonRef.current = JSON.stringify(cur, null, 2);
            setDirty(true);
          }
        }
      }
    } catch {}
    setDirty(false);
    if(onMetaChange){
      try { onMetaChange({ title: res.title || '', slug: res.slug || '', dirty:false }); } catch {}
    }
    setPreviewData(null);
    setShowRenderPreview(true);
    setBuilderTab('visual');
    setSelectedNodeId(null);
    // load versions
    if(token && p){
      setVersionsLoading(true);
      api.listPageVersions(token, p.id).then(v=>{ if(Array.isArray(v)) setVersions(v); setVersionsLoading(false); }).catch(()=>setVersionsLoading(false));
    }
  }

  async function createPage(){
    const slug = prompt('Slug?');
    const title = prompt('Title?');
    if(!slug || !title) return;
    setBusy(true);
  const res = await api.createPage(token, { slug, title });
    setBusy(false);
    if(res.error) return alert(res.error);
    setList([res, ...list]);
  }

  async function saveDraft(){
    if(!selected) return;
    // Validate required parts before saving (register mode)
    if(modeRestricted === 'register'){
      const root = draftObj?.root;
      const hasCompact = containsType(root, 'codeRequestForm');
      let ok = false;
      if(hasCompact){
        // Require compact form to include name and consent as well
        const formNode = findFirstOfType(root, 'codeRequestForm');
        const p = formNode?.props || {};
        ok = !!(p && p.showName && p.showConsent);
      } else {
        const hasEmail = containsType(root, 'codeRequestEmail');
        const hasName = containsType(root, 'codeRequestName');
        const hasConsent = containsType(root, 'codeRequestConsent');
        const hasSubmit = containsType(root, 'codeRequestSubmit');
        ok = hasEmail && hasName && hasConsent && hasSubmit;
      }
      if(!ok){
        alert(t('pagesAdmin.missingRequiredModules'));
        return; // abort save
      }
    }
    let parsed;
    if(builderTab === 'json') {
      try { parsed = JSON.parse(draftJson); } catch(e){ return alert(t('pagesAdmin.invalidJson', { message: e.message })); }
    } else {
      parsed = draftObj;
    }
    setBusy(true);
  const res = await api.updatePage(token, selected.id, { draftJson: parsed });
    setBusy(false);
    if(res.error) return alert(res.error);
    setList(list.map(p=> p.id===res.id? res : p));
    setSelected(res);
    if(onMetaChange){
      try { onMetaChange({ title: res.title || '', slug: res.slug || '', dirty:false }); } catch {}
    }
    alert(t('pagesAdmin.saved'));
    // invalidate preview
    setPreviewData(null);
    if(builderTab === 'visual') {
      try { setDraftJson(JSON.stringify(parsed, null, 2)); } catch {}
    } else {
      try { setDraftObj(parsed); } catch {}
    }
    try { originalJsonRef.current = JSON.stringify(parsed, null, 2); } catch { originalJsonRef.current=''; }
    setDirty(false);
  }

  async function publish(){
    if(!selected) return;
    // Publish-time validation (register mode): require either compact form or (email + submit)
    if(modeRestricted === 'register'){
      const hasCompact = containsType(draftObj?.root, 'codeRequestForm');
      const hasEmail = containsType(draftObj?.root, 'codeRequestEmail');
      const hasName = containsType(draftObj?.root, 'codeRequestName');
      const hasConsent = containsType(draftObj?.root, 'codeRequestConsent');
      const hasSubmit = containsType(draftObj?.root, 'codeRequestSubmit');
      let ok = false;
      if(hasCompact){
        const formNode = findFirstOfType(draftObj?.root, 'codeRequestForm');
        const p = formNode?.props || {};
        ok = !!(p.showName && p.showConsent);
      } else {
        ok = hasEmail && hasName && hasConsent && hasSubmit;
      }
      if(!ok){
        const proceed = confirm(t('pagesAdmin.autoFixPrompt'));
        if(!proceed) return; // abort publish
        // Auto-fix: add minimal email + submit under root (or first container/card)
        const cur = JSON.parse(JSON.stringify(ensureRoot()));
        if(hasCompact){
          const formNode = findFirstOfType(cur.root, 'codeRequestForm');
          if(formNode){
            formNode.props = {
              ...formNode.props,
              showName: true,
              showConsent: true,
              submitLabel: formNode.props?.submitLabel || t('common.form.confirmEmail'),
              submitLabelLoading: formNode.props?.submitLabelLoading || t('common.form.activating')
            };
          }
        } else {
          function findPlace(n){
            if(['container','card','section'].includes(n.type)) return n;
            for(const c of (n.children||[])){ const r = findPlace(c); if(r) return r; }
            return n; // fallback any node
          }
          const parent = findPlace(cur.root);
          if(!parent.children) parent.children = [];
          if(!hasEmail) parent.children.push({ id: genId(), type: 'codeRequestEmail', props: { label: 'E-Mail' } });
          if(!hasName) parent.children.push({ id: genId(), type: 'codeRequestName', props: { label: 'Name', required:true } });
          if(!hasConsent) parent.children.push({ id: genId(), type: 'codeRequestConsent', props: { label: 'Newsletter Einwilligung', required:true } });
          if(!hasSubmit) parent.children.push({ id: genId(), type: 'codeRequestSubmit', props: { submitLabel: t('common.form.confirmEmail'), submitLabelLoading: t('common.form.activating') } });
        }
        commit(cur); syncJsonFromObj(cur); setDirty(true);
        alert(t('pagesAdmin.requiredModulesAdded'));
        return;
      }
    }
    setBusy(true);
    const res = await api.publishPage(token, selected.id);
    setBusy(false);
    if(res.error) return alert(res.error);
    setList(list.map(p=> p.id===res.id? res : p));
    setSelected(res);
    if(onMetaChange){
      try { onMetaChange({ title: res.title || '', slug: res.slug || '', dirty:false }); } catch {}
    }
    alert(t('pagesAdmin.published'));
  }

  async function changeTheme(e){
    if(!selected) return;
    const newThemeId = e.target.value || null;
    setBusy(true);
    const res = await api.updatePage(token, selected.id, { themeId: newThemeId });
    setBusy(false);
    if(res.error) return alert(res.error);
    setList(list.map(p=> p.id===res.id? res : p));
    setSelected(res);
  }

  // ---- Builder helpers ----
  function ensureRoot(){
    if(!draftObj || !draftObj.root){
      const fresh = { version: 1, root: { id: genId(), type:'section', props:{}, children: [] } };
      setDraftObj(fresh); setDraftJson(JSON.stringify(fresh,null,2));
      return fresh;
    }
    return draftObj;
  }

  function genId(){ return 'n'+Math.random().toString(36).slice(2,9); }

  // Ensure every node has a unique id; assign new ids for missing/duplicate legacy nodes
  function rekeyTree(tree){
    const used = new Set();
    let changed = false;
    function walk(node){
      if(!node.id || used.has(node.id)){
        node.id = genId();
        changed = true;
      }
      used.add(node.id);
      if(Array.isArray(node.children)){
        node.children.forEach(walk);
      }
    }
    try { walk(tree.root); } catch {}
    return { changed, tree };
  }

  function addComponent(type, presetProps){
    const cur = ensureRoot();
    let realType = type;
    const extraProps = {};
    // Legacy full variant mapping
    if(type === 'codeRequestForm_full') { realType = 'codeRequestForm'; Object.assign(extraProps, { showName:true, showConsent:true, consentRequired:true, nameLabel:'Name', emailLabel:'E-Mail', consentLabel:'Ich akzeptiere den Erhalt von Infos.', submitLabel:'Code anfordern' }); }
  const modularParts = ['codeRequestName','codeRequestEmail','codeRequestConsent','codeRequestSubmit'];
  let parent;
    if(selectedNodeId){ parent = findNode(cur.root, selectedNodeId); }
    if(!parent) parent = cur.root;
    // Previously: auto-wrap modular parts in codeRequestGroup. Now: allow standalone placement.
    if(type === 'codeRequestGroup') { Object.assign(extraProps, { layout:'vertical', gap:'12px' }); }
    // If adding compact form, expand into modular parts instead
    if(realType === 'codeRequestForm'){
      if(!parent.children) parent.children = [];
      // derive labels from props
      const p = { ...defaultProps(realType), ...extraProps, ...(presetProps||{}) };
      parent.children.push({ id: genId(), type:'codeRequestEmail', props:{ label: p.emailLabel || 'E-Mail' } });
      if(p.showName){ parent.children.push({ id: genId(), type:'codeRequestName', props:{ label: p.nameLabel || 'Name' } }); }
      if(p.showConsent){ parent.children.push({ id: genId(), type:'codeRequestConsent', props:{ label: p.consentLabel || 'Ich akzeptiere den Erhalt von Infos.', required: !!p.consentRequired } }); }
      parent.children.push({ id: genId(), type:'codeRequestSubmit', props:{ submitLabel: p.submitLabel || 'Code anfordern', submitLabelLoading: p.submitLabelLoading || 'Sende...' } });
      commit(cur);
      setDirty(true);
      return;
    }
    const newNode = { id: genId(), type: realType, props: { ...defaultProps(realType), ...extraProps, ...(presetProps||{}) }, children: canHaveChildren(realType)? [] : undefined };
    if(!parent.children) parent.children = [];
    parent.children.push(newNode);
    commit(cur);
    setSelectedNodeId(newNode.id);
    setDirty(true);
  }

  function renderCraftEditor(){
    if(!selected) return <div className='p-4 text-sm text-slate-500'>{t('pagesAdmin.craft.noPage')}</div>;
    
    const hasContent = draftObj && draftObj.root;
    const theme = themes.find(t => t.id === selected.themeId) || themes[0];
    const meta = {
      company: companySelf,
      theme: theme,
    };
    
    // Render Visual Builder content in editable mode with PageContext
    return (
      <div className='flex h-full bg-slate-50'>
        <div className='w-64 border-r bg-background overflow-y-auto'>
          <div className='p-3 border-b'>
            <h3 className='text-sm font-semibold mb-2'>{t('pagesAdmin.craft.heading')}</h3>
            <p className='text-xs text-slate-600'>
              {t('pagesAdmin.craft.alt')}
            </p>
          </div>
          <CraftToolbar />
        </div>
        
        <div className='flex-1 bg-slate-100 overflow-auto p-8'>
          <div className='bg-background shadow-lg mx-auto' style={{ maxWidth: '640px', minHeight: '400px', padding: '2rem' }}>
            <PageProvider value={meta}>
              {hasContent ? (
                <PageRenderer tree={draftObj.root} />
              ) : (
                <div className='text-sm text-slate-500 text-center p-8'>
                  {t('pagesAdmin.craft.noContent')}
                </div>
              )}
            </PageProvider>
          </div>
          
          <div className='mt-4 text-center'>
            <p className='text-xs text-slate-600 mb-2'>
              {t('pagesAdmin.craft.loadedFromVisual')}
            </p>
            <p className='text-xs text-slate-500'>
              {t('pagesAdmin.craft.editInVisual')}
            </p>
          </div>
        </div>
        
        <div className='w-80 border-l bg-background p-3 overflow-y-auto'>
          <h3 className='text-sm font-semibold mb-2'>{t('pagesAdmin.craft.info')}</h3>
          <div className='text-xs text-slate-600 space-y-2'>
            <p>{t('pagesAdmin.craft.loadedFromVisual')}</p>
            <p className='mt-4 pt-4 border-t'>
              <strong>{t('pagesAdmin.craft.nextSteps')}</strong>
            </p>
            <ul className='list-disc list-inside space-y-1 text-slate-500'>
              <li>{t('pagesAdmin.craft.stepDragDrop')}</li>
              <li>{t('pagesAdmin.craft.stepInlineEdit')}</li>
              <li>{t('pagesAdmin.craft.stepPropsPanel')}</li>
            </ul>
        </div>
      </div>
    </div>
  );
}  /* Removed renderGrapesEditor function - GrapesJS integration removed */
  function renderGrapesEditor_REMOVED(){
    if(!selected) return <div className='p-4 text-[11px] text-slate-500'>{t('pagesAdmin.craft.noPage')}</div>;
    return (
      <div className='flex flex-col h-full'>
        <div className='border-b px-3 py-2 text-[11px] text-slate-600 flex items-center gap-2 bg-background'>
          <span className='font-medium'>{t('pagesAdmin.grapes.heading')}</span>
          <span className='text-slate-400'>{t('pagesAdmin.pageLabel')} {selected.title}</span>
          {modeRestricted && (
            <span className='ml-2 inline-flex items-center rounded-full border px-2 py-[2px] text-[10px] text-slate-600 bg-slate-50'>
              {t('pagesAdmin.modeLabel', { mode: modeRestricted === 'register' ? t('pagesAdmin.modeRegister') : t('pagesAdmin.modeCode') })}
            </span>
          )}
          <button
            type='button'
            className='ml-4 text-[10px] px-2 py-[2px] rounded border border-slate-300 bg-slate-50 hover:bg-slate-100'
            onClick={() => {
              if(!draftObj || !draftObj.root) return;
              const core = window.scanmireinGrapes;
              if(!core || !core.getWrapper) return;
              try {
                const wrapper = core.getWrapper();
                if(!wrapper) return;
                // Generate final HTML from the tree (matches registry.jsx output with responsive wrapper)
                const html = generateHTMLWithWrapper(draftObj.root);
                if(!html) return;
                wrapper.empty();
                wrapper.append(html);
                console.log('[Scanmirein] Visual → Grapes HTML Import abgeschlossen (1:1 mit Container)');
              } catch(e){ console.warn('Grapes HTML Import Fehler', e); }
            }}
          >
            {t('pagesAdmin.loadFromVisual')}
          </button>
        </div>
        <div className='flex-1 min-h-0'>
          <StudioEditor
            ref={grapesEditorRef}
            options={{ 
              theme: 'light',
              canvas: {
                styles: [
                  '/src/index.css'
                ],
                scripts: []
              }
            }}
            onReady={(inst) => {
              // Determine core editor object (Studio wrapper vs core instance)
              const core = inst && (inst.getEditor ? inst.getEditor() : (inst.editor || inst));
              // Global Referenz für Buttons / andere Integrationen
              if(core){
                window.scanmireinGrapes = core;
              }
              if(!core){
                console.warn('Grapes core editor not available');
                return;
              }
              // Store scanMode as a simple property instead of using .set
              core.scanMode = modeRestricted === 'code' ? 'code' : 'register';
              // Panels guard
              const pn = core.Panels;
              if(pn && pn.addPanel){
                pn.addPanel({ id: 'scanmirein-mode-switch', el: document.createElement('div') });
                const panel = pn.getPanel && pn.getPanel('scanmirein-mode-switch');
                if(panel){
                  const el = panel.get('el');
                  el.className = 'gjs-pn-panel';
                  el.style.display = 'flex';
                  el.style.alignItems = 'center';
                  el.style.gap = '4px';
                  el.style.marginRight = '8px';
                  function renderButtons(active){
                    el.innerHTML = '';
                    const btnRegister = document.createElement('button');
                    const btnCode = document.createElement('button');
                    btnRegister.textContent = 'Register-Seite';
                    btnCode.textContent = 'Code-Seite';
                    [btnRegister, btnCode].forEach((b)=>{
                      b.style.border = '1px solid #d1d5db';
                      b.style.borderRadius = '9999px';
                      b.style.padding = '4px 10px';
                      b.style.fontSize = '11px';
                      b.style.cursor = 'pointer';
                      b.style.background = '#ffffff';
                    });
                    if(active === 'register'){
                      btnRegister.style.background = '#2563eb';
                      btnRegister.style.color = '#ffffff';
                    } else {
                      btnCode.style.background = '#2563eb';
                      btnCode.style.color = '#ffffff';
                    }
                    btnRegister.onclick = () => {
                      core.scanMode = 'register';
                      renderButtons('register');
                    };
                    btnCode.onclick = () => {
                      core.scanMode = 'code';
                      renderButtons('code');
                    };
                    el.appendChild(btnRegister);
                    el.appendChild(btnCode);
                  }
                  renderButtons(modeRestricted === 'code' ? 'code' : 'register');
                  const topbar = document.querySelector('.gjs-pn-panels');
                  if(topbar){
                    topbar.appendChild(el);
                  }
                }
              }
              // Register custom blocks with core instance
              registerScanmireinBlocks(core);
            }}
          />
        </div>
      </div>
    );
  }

  function defaultProps(type){
    switch(type){
      case 'heading': return { text: 'Überschrift', level: 2 };
      case 'text': return { html: 'Text...' };
      case 'divider': return { thicknessPx: 1, style: 'solid', color: '#e5e7eb', marginY: 12 };
      case 'spacer': return { heightPx: 16 };
      case 'button': return { label: 'Button', variant:'primary' };
      case 'row': return { gapPx: 8, align: 'center', justify: 'start' };
      case 'section': return { padding: 'md' };
      case 'container': return { maxWidth: '' };
      case 'card': return { };
      case 'codeRequestForm': return { submitLabel: 'Code anfordern' };
  case 'codeDisplay': return { heading:'Ihr Code', code: 'ABCD-1234', hint: '' };
      default: return {};
    }
  }
  function canHaveChildren(type){ return ['section','container','card','row','codeRequestGroup'].includes(type); }

  function findNode(node,id){ if(!node) return null; if(node.id===id) return node; for(const c of (node.children||[])){ const f = findNode(c,id); if(f) return f; } return null; }
  function findParent(node,id,parent=null){ if(!node) return null; if(node.id===id) return parent; for(const c of (node.children||[])){ const f = findParent(c,id,node); if(f) return f; } return null; }
  function containsType(node, type){ if(!node) return false; if(node.type===type) return true; return (node.children||[]).some(c=>containsType(c,type)); }
  function findFirstOfType(node, type){ if(!node) return null; if(node.type===type) return node; for(const c of (node.children||[])){ const f = findFirstOfType(c,type); if(f) return f; } return null; }

  function commit(obj){ 
    const cloned = JSON.parse(JSON.stringify(obj)); 
    setDraftObj(cloned);
    setDirty(true);
    if(onMetaChange && selected){
      try { onMetaChange({ title: selected.title || '', slug: selected.slug || '', dirty:true }); } catch {}
    }
    // Add to history
    addToHistory(cloned);
  }
  
  // Undo/Redo System
  function addToHistory(tree) {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(tree)));
    if (newHistory.length > 50) newHistory.shift();
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }
  
  function undo() {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const state = history[newIndex];
      setDraftObj(state);
      setDraftJson(JSON.stringify(state, null, 2));
      setHistoryIndex(newIndex);
      setDirty(true);
    }
  }
  
  function redo() {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const state = history[newIndex];
      setDraftObj(state);
      setDraftJson(JSON.stringify(state, null, 2));
      setHistoryIndex(newIndex);
      setDirty(true);
    }
  }
  
  // Copy/Paste/Duplicate
  function copyNode() {
    if (!selectedNodeId || !draftObj) return;
    const node = findNode(draftObj.root, selectedNodeId);
    if (node) setClipboard(JSON.parse(JSON.stringify(node)));
  }
  
  function pasteNode() {
    if (!clipboard || !draftObj) return;
    const cur = ensureRoot();
    let parent = selectedNodeId ? findNode(cur.root, selectedNodeId) : cur.root;
    if (!parent) parent = cur.root;
    if (!parent.children) parent.children = [];
    const newNode = JSON.parse(JSON.stringify(clipboard));
    function reassignIds(node) {
      node.id = genId();
      if (node.children) node.children.forEach(reassignIds);
    }
    reassignIds(newNode);
    parent.children.push(newNode);
    commit(cur);
    setSelectedNodeId(newNode.id);
  }
  
  function duplicateNode(targetId) {
    const id = targetId || selectedNodeId;
    if (!id || !draftObj) return;
    const node = findNode(draftObj.root, id);
    if (!node) return;
    const cur = ensureRoot();
    const parent = findParent(cur.root, id);
    if (!parent || !Array.isArray(parent.children)) return;
    const idx = parent.children.findIndex(n => n.id === id);
    const clone = JSON.parse(JSON.stringify(node));
    function reassignIds(n) {
      n.id = genId();
      if (n.children) n.children.forEach(reassignIds);
    }
    reassignIds(clone);
    parent.children.splice(idx + 1, 0, clone);
    commit(cur);
    setSelectedNodeId(clone.id);
  }
  
  // Keyboard Shortcuts
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.shiftKey && e.key === 'z' || e.key === 'y')) {
        e.preventDefault();
        redo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedNodeId) {
        e.preventDefault();
        copyNode();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && clipboard) {
        e.preventDefault();
        pasteNode();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedNodeId) {
        e.preventDefault();
        duplicateNode();
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNodeId) {
        const tag = document.activeElement?.tagName?.toLowerCase();
        const isEditing = tag === 'input' || tag === 'textarea' || tag === 'select' || document.activeElement?.isContentEditable;
        if (isEditing) return;
        e.preventDefault();
        deleteNode(selectedNodeId);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (dirty) saveDraft();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodeId, clipboard, historyIndex, history, dirty, draftObj]);

  function updateNodeProps(patch){
    if(!draftObj || !selectedNodeId) return; const cur = JSON.parse(JSON.stringify(draftObj));
    const node = findNode(cur.root, selectedNodeId); if(!node){return;}
    // Normalize boolean-like select fields (per-field semantics)
    const normalized = { ...patch };
    for(const k of Object.keys(normalized)){
      const v = normalized[k];
      if(['showName','showConsent','consentRequired'].includes(k)){
        // These use '' = nein, '1' = ja
        normalized[k] = v === '1';
      } else if(['showQr','showCopyLink'].includes(k)){
        // These use '' = ja (default), '0' = nein
        if(v === '' || v === '1' || v === true) normalized[k] = true;
        else if(v === '0' || v === false) normalized[k] = false;
        else normalized[k] = !!v;
      }
    }
    node.props = { ...(node.props||{}), ...normalized };
    commit(cur); syncJsonFromObj(cur);
    setDirty(true);
  }
  function updateNodePropsById(nodeId, patch){
    if(!draftObj || !nodeId) return; const cur = JSON.parse(JSON.stringify(draftObj));
    const node = findNode(cur.root, nodeId); if(!node){return;}
    const boolKeys = ['showName','showConsent','consentRequired'];
    const normalized = { ...patch };
    for(const k of boolKeys){ if(k in normalized){ normalized[k] = normalized[k] === '1' || normalized[k] === true; } }
    node.props = { ...(node.props||{}), ...normalized };
    commit(cur); syncJsonFromObj(cur);
    setDirty(true);
  }
  const [settingsNodeId, setSettingsNodeId] = React.useState(null);
  const settingsNode = React.useMemo(()=> settingsNodeId ? findNode(draftObj?.root, settingsNodeId) : null, [draftObj, settingsNodeId]);
  function openSettings(id){ setSettingsNodeId(id); }
  function closeSettings(){ setSettingsNodeId(null); }
  function syncJsonFromObj(obj){ try { setDraftJson(JSON.stringify(obj,null,2)); } catch {} }

  // --- Cross-parent DnD helpers ---
  function removeNodeById(root, id){
    const parent = findParent(root, id);
    if(!parent){ return { parent:null, index:-1, node: root.id===id? root : null }; }
    if(!parent.children) return { parent, index:-1, node:null };
    const idx = parent.children.findIndex(c=>c.id===id);
    if(idx===-1) return { parent, index:-1, node:null };
    const [node] = parent.children.splice(idx,1);
    return { parent, index:idx, node };
  }
  function insertNodeAt(root, parentId, index, node){
    const parent = findNode(root, parentId);
    if(!parent) return false;
    if(!Array.isArray(parent.children)) parent.children = [];
    if(index<0 || index>parent.children.length) index = parent.children.length;
    parent.children.splice(index, 0, node);
    return true;
  }
  function isDescendant(root, potentialAncestorId, id){
    const anc = findNode(root, potentialAncestorId); if(!anc) return false;
    function walk(n){ if(!n) return false; if(n.id===id) return true; return (n.children||[]).some(walk); }
    return walk(anc);
  }

  function handlePreviewDragEnd({ active, over }){
    if(!over) return;
    const a = active?.id;
    const o = over?.id;
    const data = active?.data?.current || {};
    const cur = JSON.parse(JSON.stringify(ensureRoot()));
    // Palette insertion
    if(data.kind === 'palette'){
      const type = data.type;
      const presetProps = data.presetProps || undefined;
      if(typeof o === 'string'){
        if(o.startsWith('drop-child:')){
          const parentId = o.slice('drop-child:'.length);
          addChild(parentId, type, presetProps);
          return;
        }
        if(o.startsWith('drop-before:')){
          const childId = o.slice('drop-before:'.length);
          const parent = findParent(cur.root, childId); if(!parent) return;
          addSibling(parent.id, childId, type, 'before', presetProps);
          return;
        }
        if(o.startsWith('drop-after:')){
          const childId = o.slice('drop-after:'.length);
          const parent = findParent(cur.root, childId); if(!parent) return;
          addSibling(parent.id, childId, type, 'after', presetProps);
          return;
        }
      }
      // Fallback: drop on node -> insert before
      const parent = findParent(cur.root, o); if(parent) addSibling(parent.id, o, type, 'before', presetProps);
      return;
    }
    // Move existing node across parents
    const activeId = String(a);
    if(!activeId) return;
    // Prevent dropping into its own subtree
    if(typeof o === 'string'){
      if(o.startsWith('drop-child:')){
        const parentId = o.slice('drop-child:'.length);
        if(isDescendant(cur.root, activeId, parentId)) return; // cannot move parent into child
        const { node } = removeNodeById(cur.root, activeId); if(!node) return;
        insertNodeAt(cur.root, parentId, Number.MAX_SAFE_INTEGER, node);
      } else if(o.startsWith('drop-before:')){
        const refId = o.slice('drop-before:'.length);
        if(isDescendant(cur.root, activeId, refId)) return;
        const parent = findParent(cur.root, refId); if(!parent) return;
        const { node } = removeNodeById(cur.root, activeId); if(!node) return;
        const idx = parent.children.findIndex(c=>c.id===refId);
        insertNodeAt(cur.root, parent.id, idx, node);
      } else if(o.startsWith('drop-after:')){
        const refId = o.slice('drop-after:'.length);
        if(isDescendant(cur.root, activeId, refId)) return;
        const parent = findParent(cur.root, refId); if(!parent) return;
        const { node } = removeNodeById(cur.root, activeId); if(!node) return;
        const idx = parent.children.findIndex(c=>c.id===refId);
        insertNodeAt(cur.root, parent.id, idx+1, node);
      } else {
        // over is likely another node id (sortable item) -> insert before it within its parent
        if(isDescendant(cur.root, activeId, o)) return;
        const parent = findParent(cur.root, o); if(!parent) return;
        const { node } = removeNodeById(cur.root, activeId); if(!node) return;
        const idx = parent.children.findIndex(c=>c.id===o);
        insertNodeAt(cur.root, parent.id, idx, node);
      }
      commit(cur); syncJsonFromObj(cur); setDirty(true); setSelectedNodeId(activeId);
    }
  }

  function moveNode(id, dir){
    if(!draftObj) return; const cur = JSON.parse(JSON.stringify(draftObj));
    const parent = findParent(cur.root, id); const node = findNode(cur.root,id); if(!node) return;
    const list = parent? parent.children : [cur.root];
    const idx = list.findIndex(n=>n.id===id); if(idx===-1) return;
    const tgt = dir==='up'? idx-1 : idx+1;
    if(tgt<0 || tgt>=list.length) return;
    const [sp] = list.splice(idx,1); list.splice(tgt,0,sp); commit(cur); syncJsonFromObj(cur); setDirty(true);
  }
  function deleteNode(id){ if(!draftObj) return; if(draftObj.root.id===id){ toast({ title: t('common.alerts.rootCannotBeDeleted'), variant: 'destructive' }); return; } const cur = JSON.parse(JSON.stringify(draftObj)); removeNode(cur.root,id); commit(cur); syncJsonFromObj(cur); if(selectedNodeId===id) setSelectedNodeId(null); setDirty(true); }
  function removeNode(node,id){ if(!node.children) return false; const idx = node.children.findIndex(c=>c.id===id); if(idx!==-1){ node.children.splice(idx,1); return true;} for(const c of node.children){ if(removeNode(c,id)) return true; } return false; }

  const selectedNode = selectedNodeId && draftObj ? findNode(draftObj.root, selectedNodeId) : null;

  function reorderNodes(dragId, overId){
    if(!draftObj) return; if(dragId===overId) return;
    const cur = JSON.parse(JSON.stringify(draftObj));
    // Find parent arrays containing both ids (sibling-only for now)
    function findParentWith(ids, node){
      if(node.children){
        const idsIn = node.children.map(c=>c.id);
        if(ids.every(i=>idsIn.includes(i))) return node;
        for(const c of node.children){ const r = findParentWith(ids, c); if(r) return r; }
      }
      return null;
    }
    const parent = findParentWith([dragId, overId], cur.root);
    if(!parent) return; // not siblings -> ignore
    const list = parent.children;
    const from = list.findIndex(n=>n.id===dragId);
    const to = list.findIndex(n=>n.id===overId);
    if(from===-1 || to===-1) return;
    const [m] = list.splice(from,1);
    list.splice(to,0,m);
    commit(cur); syncJsonFromObj(cur); setDirty(true);
  }

  // Inline editable preview helpers
  function addSibling(parentId, refChildId, type, position, presetProps){
    const cur = JSON.parse(JSON.stringify(ensureRoot()));
    const parent = findNode(cur.root, parentId);
    if(!parent) return;
    if(!parent.children) parent.children = [];
    const idx = parent.children.findIndex(c=>c.id===refChildId);
    const extraProps = {};
    if(type==='codeRequestForm_full'){ type='codeRequestForm'; Object.assign(extraProps,{ showName:true, showConsent:true, consentRequired:true, emailLabel:'E-Mail', nameLabel:'Name', consentLabel:'Ich akzeptiere den Erhalt von Infos.', submitLabel:'Code anfordern' }); }
    if(type==='codeRequestForm'){
      const p = { ...defaultProps(type), ...extraProps, ...(presetProps||{}) };
      const parts = [
        { id: genId(), type:'codeRequestEmail', props:{ label: p.emailLabel || 'E-Mail' } },
        ...(p.showName ? [{ id: genId(), type:'codeRequestName', props:{ label: p.nameLabel || 'Name' } }] : []),
        ...(p.showConsent ? [{ id: genId(), type:'codeRequestConsent', props:{ label: p.consentLabel || 'Ich akzeptiere den Erhalt von Infos.', required: !!p.consentRequired } }] : []),
        { id: genId(), type:'codeRequestSubmit', props:{ submitLabel: p.submitLabel || 'Code anfordern', submitLabelLoading: p.submitLabelLoading || 'Sende...' } },
      ];
      parent.children.splice(position==='before'? idx: idx+1, 0, ...parts);
      commit(cur); syncJsonFromObj(cur); setDirty(true);
      return;
    }
    const newNode = { id: genId(), type, props: { ...defaultProps(type), ...extraProps, ...(presetProps||{}) }, children: canHaveChildren(type)? [] : undefined };
    parent.children.splice(position==='before'? idx: idx+1, 0, newNode);
    commit(cur); syncJsonFromObj(cur); setDirty(true); setSelectedNodeId(newNode.id);
  }
  function addChild(parentId, type, presetProps){
    const cur = JSON.parse(JSON.stringify(ensureRoot()));
    const parent = findNode(cur.root, parentId);
    if(!parent) return;
    if(!parent.children) parent.children = [];
    const extraProps = {};
    if(type==='codeRequestForm_full'){ type='codeRequestForm'; Object.assign(extraProps,{ showName:true, showConsent:true, consentRequired:true, emailLabel:'E-Mail', nameLabel:'Name', consentLabel:'Ich akzeptiere den Erhalt von Infos.', submitLabel:'Code anfordern' }); }
    if(type==='codeRequestForm'){
      if(!parent.children) parent.children = [];
      const p = { ...defaultProps(type), ...extraProps, ...(presetProps||{}) };
      parent.children.push({ id: genId(), type:'codeRequestEmail', props:{ label: p.emailLabel || 'E-Mail' } });
      if(p.showName){ parent.children.push({ id: genId(), type:'codeRequestName', props:{ label: p.nameLabel || 'Name' } }); }
      if(p.showConsent){ parent.children.push({ id: genId(), type:'codeRequestConsent', props:{ label: p.consentLabel || 'Ich akzeptiere den Erhalt von Infos.', required: !!p.consentRequired } }); }
      parent.children.push({ id: genId(), type:'codeRequestSubmit', props:{ submitLabel: p.submitLabel || 'Code anfordern', submitLabelLoading: p.submitLabelLoading || 'Sende...' } });
      commit(cur); syncJsonFromObj(cur); setDirty(true);
      return;
    }
    const newNode = { id: genId(), type, props: { ...defaultProps(type), ...extraProps, ...(presetProps||{}) }, children: canHaveChildren(type)? [] : undefined };
    parent.children.push(newNode);
    commit(cur); syncJsonFromObj(cur); setDirty(true); setSelectedNodeId(newNode.id);
  }
  function inlineDelete(id){
    // Prevent deleting required parts depending on mode and structure
    const node = findNode(draftObj?.root, id);
    if(!node){ return; }
    if(modeRestricted === 'code'){
      if(node.type==='codeDisplay'){
        toast({ title: t('common.alerts.requiredElementCannotBeDeleted'), variant: 'destructive' });
        return;
      }
    }
    deleteNode(id);
  }

  function updateNodeProps(nodeId, newProps){
    if(!draftObj) return;
    const cur = JSON.parse(JSON.stringify(draftObj));
    const node = findNode(cur.root, nodeId);
    if(!node) return;
    node.props = { ...(node.props || {}), ...newProps };
    commit(cur);
    syncJsonFromObj(cur);
    setDirty(true);
  }

  // (single unified duplicateNode now handles both overlay and keyboard cases)

  function modularizeForm(){
    if(!draftObj) return;
    const cur = JSON.parse(JSON.stringify(draftObj));
    // find first codeRequestForm
    const formNode = findFirstOfType(cur.root, 'codeRequestForm');
    if(!formNode){ toast({ title: t('common.alerts.noCompactFormFound'), variant: 'destructive' }); return; }
    // Build parts and replace compact node in-place (no group)
    const p = formNode.props || {};
    const parts = [
      { id: genId(), type:'codeRequestEmail', props:{ label: p.emailLabel || 'E-Mail', placeholder: p.emailPlaceholder || '' } },
      ...(p.showName ? [{ id: genId(), type:'codeRequestName', props:{ label: p.nameLabel || 'Name', placeholder: p.namePlaceholder || '' } }] : []),
      ...(p.showConsent ? [{ id: genId(), type:'codeRequestConsent', props:{ label: p.consentLabel || 'Ich akzeptiere den Erhalt von Infos.', required: !!p.consentRequired } }] : []),
      { id: genId(), type:'codeRequestSubmit', props:{ submitLabel: p.submitLabel || 'Code anfordern', submitLabelLoading: p.submitLabelLoading || 'Sende...' } },
    ];
    const parent = findParent(cur.root, formNode.id);
    if(parent?.children){
      const idx = parent.children.findIndex(c=>c.id===formNode.id);
      if(idx!==-1){ parent.children.splice(idx, 1, ...parts); }
    } else if(cur.root.id===formNode.id){
      // If form was the root, make a section root and put parts inside
      cur.root = { id: genId(), type:'section', props:{ padding:'md' }, children: parts };
    }
    commit(cur); syncJsonFromObj(cur); setDirty(true);
    setSelectedNodeId(parts[0]?.id || null);
    toast({ title: t('common.alerts.formConvertedToModules') });
  }

  // ---- Presets ----
  function applyPreset(kind){
    const base = { version:1, root:{ id:genId(), type:'section', props:{ padding:'lg', background:'surface' }, children: [] } };
    function push(child){ base.root.children.push(child); }
    if(kind==='request'){
      push({ id:genId(), type:'container', props:{}, children:[
        { id:genId(), type:'companyBrand', props:{ showAddress:true } },
        { id:genId(), type:'spacer', props:{ heightPx:8 } },
        { id:genId(), type:'heading', props:{ text:'Aktion – Scan', level:2 } },
        { id:genId(), type:'spacer', props:{ heightPx:8 } },
        { id:genId(), type:'text', props:{ html:'<p>Bitte E-Mail und Name angeben, um einen persönlichen Code zu erhalten.</p>' } },
        { id:genId(), type:'spacer', props:{ heightPx:12 } },
        { id:genId(), type:'codeRequestEmail', props:{ label:'E-Mail' } },
        { id:genId(), type:'spacer', props:{ heightPx:6 } },
        { id:genId(), type:'codeRequestName', props:{ label:'Name', required:true } },
        { id:genId(), type:'spacer', props:{ heightPx:6 } },
        { id:genId(), type:'codeRequestConsent', props:{ label:'Newsletter Einwilligung', required:true } },
        { id:genId(), type:'spacer', props:{ heightPx:10 } },
        { id:genId(), type:'codeRequestSubmit', props:{ submitLabel: t('common.form.confirmEmail'), submitLabelLoading: t('common.form.activating') } },
      ]});
    } else if(kind==='display') {
      push({ id:genId(), type:'container', props:{}, children:[
        { id:genId(), type:'heading', props:{ text:'Dein persönlicher Code', level:2 } },
        { id:genId(), type:'codeDisplay', props:{ hint:'Diesen Code kannst du mehrfach ansehen. Er wird bei der Einlösung durch Mitarbeiter deaktiviert.' } }
      ]});
    } else if(kind==='hero') {
      push({ id:genId(), type:'container', props:{}, children:[
        { id:genId(), type:'heading', props:{ text:'Willkommen zur Aktion', level:1 } },
        { id:genId(), type:'text', props:{ html:'<p class="mb-4">Exklusive Vorteile – sofort sichern.</p>' } },
        { id:genId(), type:'button', props:{ label:'Jetzt Starten', variant:'primary', href:'#start' } }
      ]});
    } else if(kind==='featureGrid') {
      const feature = (title, body) => ({ id:genId(), type:'container', props:{ maxWidth:'md' }, children:[
        { id:genId(), type:'heading', props:{ text:title, level:3 } },
        { id:genId(), type:'text', props:{ html:`<p>${body}</p>` } }
      ]});
      push({ id:genId(), type:'section', props:{ padding:'md' }, children:[
        feature('Vorteil 1','Kurzer Text zu Vorteil 1.'),
        feature('Vorteil 2','Kurzer Text zu Vorteil 2.'),
        feature('Vorteil 3','Kurzer Text zu Vorteil 3.')
      ]});
    }
    setDraftObj(base); try { setDraftJson(JSON.stringify(base,null,2)); } catch {}
    originalJsonRef.current = JSON.stringify(base,null,2);
    setDirty(true);
  }

  async function rollback(version){
    if(!selected) return;
    if(!confirm(`Version ${version} als neuen Draft wiederherstellen?`)) return;
    setBusy(true);
    const res = await api.rollbackPageVersion(token, selected.id, version);
    setBusy(false);
    if(res.error) return alert(res.error);
    setList(list.map(p=> p.id===res.id? res : p));
    setSelected(res);
    try { setDraftJson(JSON.stringify(res.draftJson, null, 2)); } catch {}
    try { originalJsonRef.current = JSON.stringify(res.draftJson, null, 2); } catch {}
    setDirty(false);
    // reload versions (new currentVersion)
    api.listPageVersions(token, res.id).then(v=>{ if(Array.isArray(v)) setVersions(v); });
    toast({ title: t('common.alerts.rollbackCreated') });
  }

  async function refreshPreview(){
    if(!selected) return;
    setBusy(true);
    try {
      const data = await api.previewPage(token, selected.id);
      setPreviewData(data);
    } catch(e){ /* non-fatal */ }
    setBusy(false);
  }

  // Ensure the builder preview canvas has action/company meta available:
  // whenever a page is selected and we are on the visual builder tab, fetch preview meta (silent).
  useEffect(()=>{
    let cancel = false;
    async function load(){
      try {
        if(!selected?.id || !token) return;
        if(builderTab !== 'visual') return;
        const data = await api.previewPage(token, selected.id);
        if(!cancel) setPreviewData(data);
      } catch(_) { /* non-fatal for canvas */ }
    }
    load();
    return ()=> { cancel = true; };
  }, [selected?.id, builderTab, token]);

  if(!token) return <div className='p-6 text-sm'>{t('pagesAdmin.loginRequired')}</div>;

  // Allowed components filtering for restricted modes
  let allowedComponents = null;
  if(modeRestricted === 'register'){
  allowedComponents = ['section','container','card','row','heading','text','divider','spacer','button','banner','image','video','companyLogo','companyBrand','socials','columns1','columns2','columns2_2575','columns2_7525','columns3','columns3_252550','columns3_255025','columns3_502525','columns4','countdown','codeRequestForm','codeRequestForm_full','codeRequestGroup','codeRequestName','codeRequestEmail','codeRequestConsent','codeRequestDropdown','codeRequestSubmit'];
  } else if(modeRestricted === 'code') {
  allowedComponents = ['section','container','card','row','heading','text','divider','spacer','button','banner','image','video','companyLogo','companyBrand','socials','columns1','columns2','columns2_2575','columns2_7525','columns3','columns3_252550','columns3_255025','columns3_502525','columns4','countdown','codeDisplay'];
  }
  // Dynamic locked types for required elements but still allow moving/configuring children
  let lockedTypes = [];
  if(modeRestricted === 'register'){
    // Allow deleting all form parts; validation will block save/publish if required ones are missing
    lockedTypes = [];
  } else if(modeRestricted === 'code'){
    lockedTypes = ['codeDisplay'];
  }

  return (
    <div className='flex h-[calc(100vh-60px)]'>
      {showTemplateModal && (
        <TemplateModal 
          onClose={()=>setShowTemplateModal(false)}
          onSelect={(template)=>{
            const clonedRoot = cloneTemplateWithNewIds(template.tree);
            const newDraft = { version: 1, root: clonedRoot };
            commit(newDraft);
            syncJsonFromObj(newDraft);
            setDirty(true);
            setShowTemplateModal(false);
          }}
        />
      )}
      {showImportExportModal && (
        <ImportExportModal
          onClose={()=> setShowImportExportModal(false)}
          onExport={()=>{
            try {
              const payload = {
                version: 1,
                type: 'page-template',
                page: {
                  id: selected?.id || null,
                  title: selected?.title || '',
                  slug: selected?.slug || '',
                  draftJson: draftObj || null,
                },
                meta: { exportedAt: new Date().toISOString() }
              };
              const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `page-template-${(selected?.slug || selected?.title || 'seite').toString().replace(/[^a-z0-9-_]+/gi,'_')}.json`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            } catch (e) {
              toast({ title: t('common.alerts.exportFailed'), variant: 'destructive' });
            }
          }}
          onImportClick={()=> importFileRef.current && importFileRef.current.click()}
        >
          <input ref={importFileRef} type='file' accept='application/json' className='hidden' onChange={async (e)=>{
            const file = e.target.files?.[0];
            if(!file) return;
            try{
              const text = await file.text();
              const data = JSON.parse(text);
              let tree = null;
              if (data && data.type === 'page-template' && data.page && data.page.draftJson) {
                tree = data.page.draftJson;
              } else if (data && (data.root || data.version)) {
                tree = data; // assume direct tree
              }
              if(!tree || !tree.root){
                toast({ title: t('common.alerts.invalidTemplateFormat'), variant: 'destructive' });
              } else {
                const newDraft = { version: 1, root: tree.root };
                commit(newDraft);
                syncJsonFromObj(newDraft);
                setShowImportExportModal(false);
              }
            } catch(err){
              console.error('Import error', err);
              toast({ title: t('common.alerts.importFailed'), variant: 'destructive' });
            } finally {
              if(importFileRef.current) importFileRef.current.value='';
            }
          }} />
        </ImportExportModal>
      )}
  {!forcedPageId && (
  <div className='w-64 border-r border-slate-200 p-3 flex flex-col'>
        <div className='flex items-center justify-between mb-2'>
          <h2 className='font-semibold text-sm'>{t('pagesAdmin.listTitle')}</h2>
          <button onClick={createPage} className='text-xs bg-blue-600 text-white px-2 py-1 rounded disabled:opacity-50'>{t('pagesAdmin.new')}</button>
        </div>
        {loading && <div className='text-xs text-slate-500'>{t('common.loading')}</div>}
        <div className='flex-1 overflow-y-auto space-y-1'>
          {list.map(p=> (
            <button key={p.id} onClick={()=>openPage(p)} className={`block w-full text-left px-2 py-1 rounded text-xs border ${selected?.id===p.id? 'bg-blue-50 border-blue-300':'border-slate-200 hover:bg-slate-50'}`}>
              <div className='font-medium'>{p.slug}</div>
              <div className='text-[10px] text-slate-500'>{p.status} v{p.currentVersion}</div>
            </button>
          ))}
        </div>
  </div>) }
      <div className='flex-1 flex flex-col'>
        {!selected && <div className='p-6 text-sm text-slate-500'>{t('pagesAdmin.selectOrCreate')}</div>}
        {selected && (
          <div className='flex flex-col h-full'>
            {!compactHeader && (
            <div className='p-3 border-b flex gap-2 items-center'>
              <h3 className='font-semibold text-sm flex-1 flex items-center gap-2'>
                <span>{selected.title}</span>
                <span className='text-xs text-slate-500'>{t('pagesAdmin.slugLabel', { slug: selected.slug })}</span>
                {dirty && <span className='text-[10px] px-2 py-[2px] rounded bg-amber-100 text-amber-700 border border-amber-300'>{t('pagesAdmin.unsaved')}</span>}
              </h3>
                <div className='flex items-center gap-2'>
                {/* Vorlagen Button entfernt (jetzt im Inspector als Icon) */}
                {!hideTheme && (
                  <div className='flex items-center gap-1'>
                    <label className='text-[10px] text-slate-500'>{t('pagesAdmin.theme')}</label>
                    <select value={selected.themeId || ''} onChange={changeTheme} disabled={busy || themesLoading} className='text-xs border rounded px-1 py-[2px]'>
                      <option value=''>—</option>
                      {themes.map(t=> <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                )}
                {modeRestricted==='register' && !containsType(draftObj?.root, 'codeRequestGroup') && containsType(draftObj?.root, 'codeRequestForm') && (
                  <button type='button' onClick={modularizeForm} className='text-[10px] bg-amber-200 hover:bg-amber-300 px-2 py-[3px] rounded'>{t('pagesAdmin.modularizeForm')}</button>
                )}
              </div>
              {/* Toolbar bereinigt: Undo/Redo/Copy/Paste/Duplicate entfernt (jetzt rechts als Icons) */}
              {/* Tree/Render toggle entfernt - Navigator jetzt im Inspector */}
            </div>
            )}
            <div className={fullscreenEditor ? 'fixed inset-0 z-40 bg-background flex flex-1 overflow-hidden border-t' : 'flex flex-1 overflow-hidden border-t'}>
              {/* Builder / JSON Editor */}
              <div className='flex flex-col flex-1'>
                {builderTab==='visual' && (
                  <div className='flex flex-1 overflow-hidden'>
                    {showPalette ? (
                      <div className='relative flex'>
                        <div style={{width: paletteWidth+'px'}} className='shrink-0'>
                          <BuilderPalette onAdd={addComponent} allowed={allowedComponents} draggable />
                        </div>
                        <div
                          className='w-1 cursor-col-resize bg-slate-200 hover:bg-blue-400 transition-colors shrink-0'
                          onMouseDown={(e)=>{ resizeStartX.current = e.clientX; resizeStartWidth.current = paletteWidth; setResizing('palette'); }}
                          title='Palette Breite anpassen'
                        />
                        <button
                          type='button'
                          onClick={()=>setShowPalette(false)}
                          className='absolute top-1 right-1 text-[11px] px-2 py-[2px] rounded border bg-background hover:bg-slate-50 z-10'
                          title='Palette einklappen'
                          aria-label='Palette einklappen'
                        >−</button>
                      </div>
                    ) : (
                      <div className='border-r w-6 shrink-0 bg-background flex flex-col items-center justify-start py-2 gap-2'>
                        <button
                          type='button'
                          onClick={()=>setShowPalette(true)}
                          className='text-[12px] w-5 h-5 rounded border bg-background hover:bg-slate-50 flex items-center justify-center'
                          title='Palette ausklappen'
                          aria-label='Palette ausklappen'
                        >+</button>
                        <div className='rotate-90 text-[10px] text-slate-400 whitespace-nowrap mt-4 pointer-events-none select-none'>{t('pagesAdmin.palette')}</div>
                      </div>
                    )}
                    <div className='flex flex-col flex-1 border-r'>
                      <div className='px-3 py-2 text-[11px] font-semibold border-b bg-slate-50 flex items-center justify-between'>
                        <span>{t('pagesAdmin.previewTitle')} {fullscreenEditor && <span className='text-[10px] text-slate-500 font-normal ml-2'>{t('pagesAdmin.escToClose')}</span>}</span>
                        <div className='flex items-center gap-2'>
                          <button
                            onClick={()=>setShowTemplateModal(true)}
                            disabled={busy}
                            title='Vorlagen'
                            className='h-6 px-2 flex items-center justify-center text-[11px] rounded border bg-background hover:bg-purple-50 text-purple-600 disabled:opacity-30 disabled:cursor-not-allowed'
                          >⊞ Vorlagen</button>
                            <button
                              onClick={()=> setShowImportExportModal(true)}
                              disabled={busy || !selected}
                              title='Import/Export'
                              className='h-6 px-2 flex items-center justify-center text-[11px] rounded border bg-background hover:bg-purple-50 text-purple-600 disabled:opacity-30 disabled:cursor-not-allowed'
                            >⇅ Import/Export</button>
                          <button
                            onClick={saveDraft}
                            disabled={busy || !dirty}
                            title='Speichern'
                            className='h-6 px-2 flex items-center justify-center text-[11px] rounded border bg-background hover:bg-slate-50 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed'
                          >💾 Speichern</button>
                          <button
                            onClick={()=>{ if(selected){ window.open(`/preview/${selected.id}`, '_blank'); } }}
                            disabled={busy || !selected}
                            title='Preview'
                            className='h-6 px-2 flex items-center justify-center text-[11px] rounded border bg-background hover:bg-slate-50 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed'
                          >👁 Preview</button>
                          <button
                            onClick={publish}
                            disabled={busy}
                            title='Publish'
                            className='h-6 px-2 flex items-center justify-center text-[11px] rounded border bg-background hover:bg-green-50 text-green-600 disabled:opacity-30 disabled:cursor-not-allowed'
                          >↑ Publish</button>
                          <button
                            onClick={()=>{
                              setShowVersionsModal(true);
                              if(selected?.id){
                                setVersionsLoading(true);
                                api.listPageVersions(token, selected.id).then(v=>{
                                  if(Array.isArray(v)) setVersions(v);
                                  setVersionsLoading(false);
                                }).catch(()=>setVersionsLoading(false));
                              }
                            }}
                            disabled={!selected}
                            title='Versionen'
                            className='h-6 px-2 flex items-center justify-center text-[11px] rounded border bg-background hover:bg-slate-50 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed'
                          >📋 Versionen</button>
                          <button
                            onClick={()=> setFullscreenEditor(f=>!f)}
                            title={fullscreenEditor ? 'Fullscreen verlassen (Esc)' : 'Fullscreen'}
                            className='h-6 px-2 flex items-center justify-center text-[11px] rounded border bg-background hover:bg-slate-50 text-slate-600'
                          >{fullscreenEditor ? '🗗' : '⛶'}</button>
                        </div>
                      </div>
                      <div className='flex-1 overflow-auto p-4 bg-slate-50'>
                        {modeRestricted==='register' && draftObj?.root && (()=>{
                          const hasCompact = containsType(draftObj.root, 'codeRequestForm');
                          const hasEmail = containsType(draftObj.root, 'codeRequestEmail');
                          const hasName = containsType(draftObj.root, 'codeRequestName');
                          const hasConsent = containsType(draftObj.root, 'codeRequestConsent');
                          const hasSubmit = containsType(draftObj.root, 'codeRequestSubmit');
                          let ok = false;
                          if(hasCompact){ const f = findFirstOfType(draftObj.root,'codeRequestForm'); const p=f?.props||{}; ok = !!(p.showName && p.showConsent); } else { ok = hasEmail && hasName && hasConsent && hasSubmit; }
                          if(!ok){
                            return (
                              <div className='mb-3 p-2 border border-amber-300 bg-amber-50 rounded text-[11px] text-amber-800 flex items-center justify-between'>
                                <span>{t('pagesAdmin.warnMissingRequiredModules')}</span>
                                <div className='flex gap-2'>
                                  <button onClick={()=>{
                                    const cur = JSON.parse(JSON.stringify(ensureRoot()));
                                    if(hasCompact){
                                      const formNode = findFirstOfType(cur.root, 'codeRequestForm');
                                      if(formNode){
                                        formNode.props = {
                                          ...formNode.props,
                                          showName: true,
                                          showConsent: true,
                                          submitLabel: formNode.props?.submitLabel || t('common.form.confirmEmail'),
                                          submitLabelLoading: formNode.props?.submitLabelLoading || t('common.form.activating')
                                        };
                                      }
                                    } else {
                                      function findPlace(n){ if(['container','card','section'].includes(n.type)) return n; for(const c of (n.children||[])){ const r = findPlace(c); if(r) return r; } return n; }
                                      const parent = findPlace(cur.root); if(!parent.children) parent.children=[];
                                      if(!hasEmail) parent.children.push({ id: genId(), type:'codeRequestEmail', props:{ label:'E-Mail' } });
                                      if(!hasName) parent.children.push({ id: genId(), type:'codeRequestName', props:{ label:'Name', required:true } });
                                      if(!hasConsent) parent.children.push({ id: genId(), type:'codeRequestConsent', props:{ label:'Newsletter Einwilligung', required:true } });
                                      if(!hasSubmit) parent.children.push({ id: genId(), type:'codeRequestSubmit', props:{ submitLabel: t('common.form.confirmEmail'), submitLabelLoading: t('common.form.activating') } });
                                    }
                                    commit(cur); syncJsonFromObj(cur); setDirty(true);
                                  }} className='px-2 py-[3px] rounded bg-amber-600 text-white'>Auto-fix einfügen</button>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        <div className='flex h-full border rounded-lg overflow-hidden bg-background shadow-sm'>
                          {/* Canvas */}
                          <div className='flex-1 overflow-auto bg-slate-50 flex flex-col'>
                            {/* Responsive Mode Toolbar */}
                            <div className='flex items-center gap-2 px-4 py-2 border-b bg-background'>
                                <span className='text-[11px] font-semibold text-slate-700 mr-2'>Ansicht:</span>
                                <select value={responsiveMode} onChange={(e)=>setResponsiveMode(e.target.value)} className='text-xs border rounded px-2 py-1 bg-background'>
                                  <option value='desktop'>Desktop</option>
                                  <option value='tablet'>Tablet</option>
                                  <option value='mobile'>Mobile</option>
                                </select>
                                <span className='text-[11px] font-semibold text-slate-700 ml-4 mr-2'>Editor:</span>
                                <select value={builderTab} onChange={(e)=>setBuilderTab(e.target.value)} className='text-xs border rounded px-2 py-1 bg-background'>
                                  <option value='visual'>Visual Builder</option>
                                  <option value='json'>JSON Editor</option>
                                </select>
                              </div>
                              <div className='flex-1 overflow-auto flex justify-center items-start p-6'>
                                <div className={`w-full ${responsiveMode==='tablet'?'max-w-[768px]':responsiveMode==='mobile'?'max-w-[375px]':'max-w-3xl'} transition-all`}>
                                  {draftObj?.root ? (
                                  <PageProvider value={{ action: previewData?.action || null, company: (previewData?.company || companySelf || null) }}>
                                    <DndContext
                                      sensors={sensors}
                                      collisionDetection={closestCenter}
                                      onDragStart={(e)=> setActiveDragId(String(e.active.id))}
                                      onDragEnd={(e)=>{ setActiveDragId(null); handlePreviewDragEnd(e); }}
                                      onDragCancel={()=> setActiveDragId(null)}
                                    >
                                      <EditableRenderer
                                        tree={draftObj}
                                        selectedId={selectedNodeId}
                                        setSelectedId={setSelectedNodeId}
                                        onReorder={(drag,over)=>reorderNodes(drag, over)}
                                        onAddChild={(parent,type)=>addChild(parent, type)}
                                        onAddSibling={(parentId, refChildId, type, position)=>addSibling(parentId, refChildId, type, position)}
                                        onDelete={inlineDelete}
                                        onDuplicate={duplicateNode}
                                        onOpenSettings={openSettings}
                                        onUpdateNodeProps={updateNodeProps}
                                        allowedComponents={allowedComponents || []}
                                        lockedTypes={lockedTypes}
                                      />
                                      <DragOverlay dropAnimation={{ duration: 150 }}>
                                        {activeDragId ? <DragGhost tree={draftObj} id={activeDragId} /> : null}
                                      </DragOverlay>
                                    </DndContext>
                                  </PageProvider>
                                ) : (
                                  <div className='text-[11px] text-slate-500'>Kein Inhalt</div>
                                )}
                                <div className='mt-3 text-[11px] text-slate-500'>Hinweis: Vorschau öffnet in neuem Tab. Änderungen speichern, falls benötigt.</div>
                                {dirty && <div className='mt-2 text-[10px] text-amber-600'>Lokale Änderungen noch nicht gespeichert.</div>}
                              </div>
                            </div>
                          </div>

                          {/* Inspector rechts */}
                            <div
                              className='w-1 cursor-col-resize bg-slate-200 hover:bg-blue-400 transition-colors shrink-0'
                              onMouseDown={(e)=>{ resizeStartX.current = e.clientX; resizeStartWidth.current = inspectorWidth; setResizing('inspector'); }}
                              title='Inspector Breite anpassen'
                            />
                            <div style={{width: inspectorWidth+'px'}} className='border-l bg-background flex flex-col shrink-0'>
                              <div className='px-4 py-2 border-b text-[11px] flex flex-col gap-1'>
                                {/* Tab Switcher */}
                                <div className='flex items-center gap-1 mb-1 pb-1 border-b'>
                                  <button
                                    onClick={()=>setInspectorView('config')}
                                    className={`px-2 py-1 text-[11px] rounded ${inspectorView==='config' ? 'bg-blue-100 text-blue-700 font-semibold' : 'text-slate-600 hover:bg-slate-100'}`}
                                  >Config</button>
                                  <button
                                    onClick={()=>setInspectorView('navigator')}
                                    className={`px-2 py-1 text-[11px] rounded ${inspectorView==='navigator' ? 'bg-blue-100 text-blue-700 font-semibold' : 'text-slate-600 hover:bg-slate-100'}`}
                                  >Navigator</button>
                                </div>
                                <div className='flex items-center justify-end flex-wrap gap-1'>
                                  <button
                                    onClick={undo}
                                    disabled={historyIndex === 0}
                                    aria-label='Undo'
                                    title='Undo (Ctrl+Z)'
                                    className='h-7 w-7 flex items-center justify-center text-[11px] rounded border bg-background hover:bg-slate-50 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400'
                                  >↺</button>
                                  <button
                                    onClick={redo}
                                    disabled={historyIndex >= history.length - 1}
                                    aria-label='Redo'
                                    title='Redo (Ctrl+Y)'
                                    className='h-7 w-7 flex items-center justify-center text-[11px] rounded border bg-background hover:bg-slate-50 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400'
                                  >↻</button>
                                  <button
                                    onClick={copyNode}
                                    disabled={!selectedNodeId}
                                    aria-label='Copy'
                                    title='Copy (Ctrl+C)'
                                    className='h-7 w-7 flex items-center justify-center text-[12px] rounded border bg-background hover:bg-slate-50 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400'
                                  >📄</button>
                                  <button
                                    onClick={pasteNode}
                                    disabled={!clipboard}
                                    aria-label='Paste'
                                    title='Paste (Ctrl+V)'
                                    className='h-7 w-7 flex items-center justify-center text-[12px] rounded border bg-background hover:bg-slate-50 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400'
                                  >📋</button>
                                  <button
                                    onClick={duplicateNode}
                                    disabled={!selectedNodeId}
                                    aria-label='Duplicate'
                                    title='Duplicate (Ctrl+D)'
                                    className='h-7 w-7 flex items-center justify-center text-[12px] rounded border bg-background hover:bg-slate-50 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400'
                                  >➕</button>
                                  
                                  <button
                                    className='h-7 px-2 flex items-center justify-center text-[11px] rounded border bg-background hover:bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-400'
                                    onClick={closeSettings}
                                    aria-label={t('common.pagebuilder.closeSettings')}
                                    title={t('common.pagebuilder.closeSettings')}
                                  >Clear</button>
                                </div>
                              </div>
                              <div className='flex-1 overflow-auto'>
                                {inspectorView === 'config' ? (
                                  settingsNode ? (
                                    <NodeSettingsModal
                                      node={settingsNode}
                                      onChange={(patch)=> updateNodePropsById(settingsNodeId, patch)}
                                      onClose={null}
                                      currentPageId={selected?.id}
                                      activeTab={inspectorTab}
                                      onTabChange={setInspectorTab}
                                    />
                                  ) : (
                                    <div className='h-full flex items-center justify-center text-[11px] text-slate-500 px-4 text-center'>
                                      Element im Canvas oder Navigator auswählen, um Einstellungen zu bearbeiten.
                                    </div>
                                  )
                                ) : (
                                  <div className='h-full flex flex-col'>
                                    {draftObj?.root ? (
                                      <TreeView
                                        root={draftObj.root}
                                        selectedId={selectedNodeId}
                                        onSelect={setSelectedNodeId}
                                        onMoveUp={(id)=>{ /* optional: implement node move up */ }}
                                        onMoveDown={(id)=>{ /* optional: implement node move down */ }}
                                        onDelete={inlineDelete}
                                        onReorder={(dragId, overId)=>reorderNodes(dragId, overId)}
                                      />
                                    ) : (
                                      <div className='p-4 text-[11px] text-slate-500'>Kein Inhalt</div>
                                    )}
                                  </div>
                                )}
                              </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Properties sidebar jetzt als fester Inspector integriert */}
                  </div>
                )}
                {builderTab==='json' && (
                  <div className='flex h-full border rounded-lg overflow-hidden bg-background shadow-sm'>
                    <div className='flex-1 overflow-auto bg-slate-50 flex flex-col'>
                      <div className='flex items-center gap-2 px-4 py-2 border-b bg-background'>
                        <span className='text-[11px] font-semibold text-slate-700 mr-2'>Editor:</span>
                        <select value={builderTab} onChange={(e)=>setBuilderTab(e.target.value)} className='text-xs border rounded px-2 py-1 bg-background'>
                          <option value='visual'>Visual Builder</option>
                          <option value='json'>JSON Editor</option>
                        </select>
                      </div>
                      <textarea value={draftJson} onChange={e=>{ 
                        setDraftJson(e.target.value); 
                        try { setDraftObj(JSON.parse(e.target.value)); } catch {} 
                        setDirty(true); 
                        if(onMetaChange && selected){ try { onMetaChange({ title: selected.title||'', slug: selected.slug||'', dirty:true }); } catch {} }
                      }} className='flex-1 w-full font-mono text-xs p-3 outline-none resize-none' spellCheck={false} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Versions Modal */}
      {showVersionsModal && (
        <div className='fixed inset-0 z-50 bg-black/30 flex items-center justify-center' onClick={()=>setShowVersionsModal(false)}>
          <div className='bg-background rounded-lg shadow-xl w-[600px] max-w-[95vw] max-h-[85vh] flex flex-col' onClick={(e)=> e.stopPropagation()}>
            <div className='px-4 py-3 border-b flex items-center justify-between'>
              <div className='font-semibold text-sm'>Versionen</div>
              <button className='text-xs px-2 py-1 rounded border bg-background hover:bg-slate-50' onClick={()=>setShowVersionsModal(false)}>{t('common.close')}</button>
            </div>
            <div className='flex-1 overflow-y-auto p-4'>
              {versionsLoading && <div className='text-[11px] text-slate-500'>Lade...</div>}
              {!versionsLoading && versions.length === 0 && <div className='text-[11px] text-slate-400'>Keine Versionen</div>}
              <div className='space-y-2'>
                {versions.map(v => (
                  <div key={v.id} className='border rounded bg-background hover:bg-slate-50 transition flex flex-col p-3 gap-2'>
                    <div className='flex items-center justify-between'>
                      <span className='text-[11px] font-medium'>Version {v.version}{v.published? ' (Publiziert)':''}</span>
                      <button 
                        onClick={()=>{
                          setShowVersionsModal(false);
                          rollback(v.version);
                        }} 
                        className='text-[10px] px-2 py-1 rounded border bg-slate-200 hover:bg-slate-300'
                      >Rollback</button>
                    </div>
                    <div className='text-[9px] text-slate-500'>{new Date(v.createdAt).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Preview({ jsonText }){
  let tree;
  try { tree = JSON.parse(jsonText); } catch { return <div className='text-xs text-red-600'>JSON ungültig</div>; }
  if(!tree.root) return <div className='text-xs text-slate-500'>Kein root</div>;
  // Minimal inline renderer duplication (avoid circular import heavy) – could reuse main renderer
  return (
    <div className='text-xs'>
      <Node node={tree.root} depth={0} />
    </div>
  );
}

function Node({ node, depth }){
  return (
    <div className='mb-2'>
      <div className='font-semibold'>{'—'.repeat(depth)} {node.type}</div>
      {node.children && node.children.map((c,i)=>(<Node key={i} node={c} depth={depth+1} />))}
    </div>
  );
}

function RenderedPreview({ preview, rawJson }){
  // Use server-provided preview.page but allow local unsaved JSON override if JSON is valid
  let localTree = null;
  try { const parsed = JSON.parse(rawJson); if(parsed && parsed.root) localTree = parsed; } catch {}
  const pageTree = localTree || preview.page;
  const replacedTree = useMemo(()=>{
    if(!pageTree) return pageTree;
    const action = preview.action || {};
    const company = preview.company || {};
    const tokens = {
      '{{action.title}}': action.title || '',
      '{{action.companyName}}': action.companyName || action.company?.name || '',
      '{{action.campaignName}}': action.campaignName || action.campaign?.name || '',
      '{{action.discountType}}': action.discountType || '',
      '{{action.discountValue}}': action.discountValue != null ? String(action.discountValue) : '',
      '{{company.addressLine1}}': company.addressLine1 || '',
      '{{company.addressLine2}}': company.addressLine2 || '',
      '{{company.postalCode}}': company.postalCode || '',
      '{{company.city}}': company.city || '',
      '{{company.country}}': company.country || '',
    };
    const str = JSON.stringify(pageTree);
    const replaced = Object.keys(tokens).reduce((acc,k)=>acc.split(k).join(tokens[k]), str);
    try { return JSON.parse(replaced); } catch { return pageTree; }
  }, [pageTree, preview.action]);

  // Inject theme similar to PublicPage (colors + Google Fonts)
  if (preview.theme) {
    const styleElId = 'pb-theme';
    let el = document.getElementById(styleElId);
    if (!el) { el = document.createElement('style'); el.id = styleElId; document.head.appendChild(el); }
    const colors = preview.theme.colors || {};
    const headingFont = preview.theme.typography?.headingFont || '';
    const bodyFont = preview.theme.typography?.bodyFont || '';
    const bodyStack = `var(--pb-font-body, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif)`;
    const headingStack = `var(--pb-font-heading, inherit)`;
    el.innerHTML = `:root{--pb-primary:${colors.primary || '#2563eb'};--pb-surface:${colors.surface || '#ffffff'};${bodyFont?`--pb-font-body:'${bodyFont}', system-ui;`:''}${headingFont?`--pb-font-heading:'${headingFont}', ${bodyFont || 'system-ui'};`:''}}
      .pb-root{ font-family: ${bodyStack}; }
      .pb-root h1,.pb-root h2,.pb-root h3,.pb-root h4,.pb-root h5,.pb-root h6{ font-family: ${headingStack}; }`;
    const addFontLink = (family, id)=>{
      if (!family) return;
      if (document.getElementById(id)) return;
      const f = family.replace(/ /g, '+');
      const link = document.createElement('link');
      link.id = id; link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${f}:wght@400;600;700&display=swap`;
      document.head.appendChild(link);
    };
    addFontLink(headingFont, 'pb-font-link-heading');
    addFontLink(bodyFont, 'pb-font-link-body');
  }

  if(!replacedTree) return <div className='text-[11px] text-slate-500'>Keine Preview Daten</div>;
  return (
    <div className='bg-background rounded border p-3'>
      <div className='text-[10px] text-slate-500 mb-2'>Preview (Draft, unveröffentlicht)</div>
      <PageRenderer tree={replacedTree} meta={{ action: preview.action || null, company: preview.company || null }} />
    </div>
  );
}

function DragGhost({ tree, id }){
  function find(n){ if(!n) return null; if(n.id===id) return n; for(const c of (n.children||[])){ const r = find(c); if(r) return r; } return null; }
  const node = find(tree?.root);
  if(!node) return null;
  return (
    <div className='rounded border bg-background px-2 py-1 text-[11px] shadow pointer-events-none opacity-90'>
      {node.type}
    </div>
  );
}

function TemplateModal({ onClose, onSelect }){
  const { token } = useAuth();
  const [activeTab, setActiveTab] = React.useState('presets'); // 'presets' | 'actions'
  const [previewTemplate, setPreviewTemplate] = React.useState(null);
  const [actions, setActions] = React.useState([]);
  const [actionsLoading, setActionsLoading] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all'); // 'all' | 'active' | 'inactive'
  
  // Load actions when switching to actions tab
  React.useEffect(() => {
    if (activeTab === 'actions' && actions.length === 0) {
      setActionsLoading(true);
      Promise.all([
        api.listActions(token),
        api.listPages(token)
      ])
        .then(([actionsData, pagesData]) => {
          console.log('Loaded actions:', actionsData);
          console.log('Loaded pages:', pagesData);
          // API returns array directly, not wrapped object
          const actionsList = Array.isArray(actionsData) ? actionsData : (actionsData.actions || []);
          const pagesList = Array.isArray(pagesData) ? pagesData : (pagesData.pages || []);
          
          // Create pages map for quick lookup
          const pagesMap = new Map();
          pagesList.forEach(page => pagesMap.set(page.id, page));
          
          // Attach pages to actions
          const actionsWithPages = actionsList.map(action => ({
            ...action,
            landingPage: action.landingPageId ? pagesMap.get(action.landingPageId) : null,
            displayPage: action.displayPageId ? pagesMap.get(action.displayPageId) : null
          }));
          
          console.log('Actions with pages:', actionsWithPages);
          setActions(actionsWithPages);
        })
        .catch(err => console.error('Failed to load actions:', err))
        .finally(() => setActionsLoading(false));
    }
  }, [activeTab, token]);
  
  // Filter actions based on search and status
  const filteredActions = React.useMemo(() => {
    return actions.filter(action => {
      const matchesSearch = !searchQuery || (action.title || action.name || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && action.active) ||
        (statusFilter === 'inactive' && !action.active);
      return matchesSearch && matchesStatus;
    });
  }, [actions, searchQuery, statusFilter]);
  
  const handleSelectAction = (action) => {
    // Convert action page to template format
    console.log('Selected action:', action);
    const pageData = action.landingPage || action.displayPage;
    console.log('Page data:', pageData);
    if (!pageData?.treeJson) {
      toast({ title: t('common.alerts.actionHasNoDesign'), variant: 'destructive' });
      return;
    }
    
    try {
      const tree = JSON.parse(pageData.treeJson);
      const template = {
        id: `action-${action.id}`,
        name: action.title || action.name,
        description: `Aus Aktion: ${action.title || action.name}`,
        thumbnail: '🎯',
        tree: tree.root || tree
      };
      onSelect(template);
    } catch (err) {
      console.error('Failed to parse action design:', err);
      toast({ title: t('common.alerts.errorLoadingDesign'), variant: 'destructive' });
    }
  };
  
  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4' onClick={onClose}>
      <div className='bg-background rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col' onClick={(e)=>e.stopPropagation()}>
        {/* Header */}
        <div className='px-6 py-4 border-b flex items-center justify-between'>
          <h3 className='text-lg font-semibold'>Vorlage auswählen</h3>
          <button onClick={onClose} className='text-slate-400 hover:text-slate-600 text-2xl leading-none'>&times;</button>
        </div>
        
        {/* Tabs */}
        <div className='border-b bg-slate-50'>
          <div className='px-6 flex gap-4'>
            <button
              onClick={() => setActiveTab('presets')}
              className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'presets' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-slate-600 hover:text-slate-800'
              }`}
            >
              Presets
            </button>
            <button
              onClick={() => setActiveTab('actions')}
              className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'actions' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-slate-600 hover:text-slate-800'
              }`}
            >
              Aktionen
            </button>
          </div>
        </div>
        
        {/* Search & Filters (only for actions tab) */}
        {activeTab === 'actions' && (
          <div className='px-6 py-4 border-b bg-background flex gap-4 items-center'>
            <div className='flex-1'>
              <input
                type='text'
                placeholder='Nach Aktionsnamen suchen...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className='px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              <option value='all'>Alle Aktionen</option>
              <option value='active'>Aktiv</option>
              <option value='inactive'>Inaktiv</option>
            </select>
          </div>
        )}
        
        {/* Content Area */}
        <div className='flex-1 overflow-y-auto p-6'>
          {activeTab === 'presets' ? (
            // Presets Grid
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
              {getLocalizedTemplates(t).map(template => (
                <div 
                  key={template.id}
                  className='relative group'
                >
                  <div className='border rounded-lg overflow-hidden bg-background hover:shadow-lg transition-shadow'>
                    {/* Preview Image/Thumbnail */}
                    <div className='aspect-video bg-slate-50 border-b relative overflow-hidden'>
                      <div className='absolute inset-0 scale-[0.4] origin-top-left pointer-events-none'>
                        <PageRenderer 
                          tree={template.tree.id === 'root' ? { root: template.tree } : template.tree} 
                          meta={{ action: null, company: null }} 
                        />
                      </div>
                      {/* Eye Icon Overlay */}
                      <div className='absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all flex items-center justify-center'>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewTemplate(template);
                          }}
                          className='opacity-0 group-hover:opacity-100 transition-opacity bg-background/90 hover:bg-background p-3 rounded-full shadow-lg'
                          title='Vorschau anzeigen'
                        >
                          <svg className='w-6 h-6 text-slate-700' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    {/* Card Content */}
                    <div className='p-4'>
                      <div className='flex items-start justify-between mb-2'>
                        <div className='text-3xl'>{template.thumbnail}</div>
                      </div>
                      <h4 className='font-semibold text-sm mb-1'>{template.name}</h4>
                      <p className='text-xs text-slate-600 mb-3'>{template.description}</p>
                      <button
                        onClick={() => onSelect(template)}
                        className='w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors'
                      >
                        Verwenden
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Actions Grid
            <>
              {actionsLoading ? (
                <div className='text-center py-12'>
                  <div className='text-slate-500'>Lade Aktionen...</div>
                </div>
              ) : filteredActions.length === 0 ? (
                <div className='text-center py-12'>
                  <div className='text-slate-500'>
                    {searchQuery || statusFilter !== 'all' 
                      ? 'Keine Aktionen gefunden' 
                      : 'Keine Aktionen vorhanden'}
                  </div>
                </div>
              ) : (
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                  {filteredActions.map(action => {
                    const pageData = action.landingPage || action.displayPage;
                    const hasDesign = pageData?.treeJson;
                    
                    return (
                      <div 
                        key={action.id}
                        className='relative group'
                      >
                        <div className='border rounded-lg overflow-hidden bg-background hover:shadow-lg transition-shadow'>
                          {/* Preview */}
                          <div className='aspect-video bg-slate-50 border-b relative overflow-hidden'>
                            {hasDesign ? (
                              <>
                                <div className='absolute inset-0 scale-[0.4] origin-top-left pointer-events-none'>
                                  <PageRenderer 
                                    tree={(() => {
                                      try {
                                        const tree = JSON.parse(pageData.treeJson);
                                        return tree.root ? { root: tree.root } : tree;
                                      } catch {
                                        return { root: { id: 'root', type: 'root', children: [] }};
                                      }
                                    })()} 
                                    meta={{ action: null, company: null }} 
                                  />
                                </div>
                                {/* Eye Icon Overlay */}
                                <div className='absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all flex items-center justify-center'>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      try {
                                        const tree = JSON.parse(pageData.treeJson);
                                        setPreviewTemplate({
                                          id: `action-${action.id}`,
                                          name: action.title || action.name,
                                          description: `Aus Aktion: ${action.title || action.name}`,
                                          thumbnail: '🎯',
                                          tree: tree.root || tree
                                        });
                                      } catch (err) {
                                        console.error('Failed to parse action design:', err);
                                      }
                                    }}
                                    className='opacity-0 group-hover:opacity-100 transition-opacity bg-background/90 hover:bg-background p-3 rounded-full shadow-lg'
                                    title='Vorschau anzeigen'
                                  >
                                    <svg className='w-6 h-6 text-slate-700' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
                                    </svg>
                                  </button>
                                </div>
                              </>
                            ) : (
                              <div className='absolute inset-0 flex items-center justify-center text-slate-400 text-sm'>
                                Kein Design verfügbar
                              </div>
                            )}
                          </div>
                          
                          {/* Card Content */}
                          <div className='p-4'>
                            <div className='flex items-start justify-between mb-2'>
                              <div className='text-2xl'>🎯</div>
                              <span className={`text-xs px-2 py-1 rounded ${action.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                {action.active ? 'Aktiv' : 'Inaktiv'}
                              </span>
                            </div>
                            <h4 className='font-semibold text-sm mb-1'>{action.title || action.name || 'Unbenannt'}</h4>
                            <p className='text-xs text-slate-600 mb-3'>Aktion #{action.id}</p>
                            <button
                              onClick={() => handleSelectAction(action)}
                              disabled={!hasDesign}
                              className={`w-full px-3 py-2 text-white text-xs font-medium rounded transition-colors ${
                                hasDesign 
                                  ? 'bg-blue-600 hover:bg-blue-700' 
                                  : 'bg-slate-300 cursor-not-allowed'
                              }`}
                            >
                              {hasDesign ? 'Verwenden' : 'Kein Design'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Click Preview Modal */}
      {previewTemplate && (
        <div 
          className='fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-8'
          onClick={(e) => {
            e.stopPropagation();
            setPreviewTemplate(null);
          }}
        >
          <div className='bg-background rounded-lg shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden' onClick={(e) => e.stopPropagation()}>
            <div className='px-6 py-4 border-b flex items-center justify-between bg-slate-50'>
              <div>
                <h4 className='font-semibold text-base'>{previewTemplate.name}</h4>
                <p className='text-sm text-slate-600'>{previewTemplate.description}</p>
              </div>
              <div className='flex items-center gap-2'>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(previewTemplate);
                  }}
                  className='px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors'
                >
                  Verwenden
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewTemplate(null);
                  }}
                  className='text-slate-400 hover:text-slate-600 text-2xl leading-none px-2'
                >
                  &times;
                </button>
              </div>
            </div>
            <div className='p-6 overflow-y-auto max-h-[calc(85vh-80px)] bg-slate-50'>
              <div className='bg-background rounded border shadow-sm p-4'>
                <PageRenderer 
                  tree={previewTemplate.tree.id === 'root' ? { root: previewTemplate.tree } : previewTemplate.tree} 
                  meta={{ action: null, company: null }} 
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ImportExportModal({ onClose, onExport, onImportClick, children }){
  const { t } = useTranslation();
  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      <div className='absolute inset-0 bg-black/30' onClick={onClose} />
      <div className='relative bg-background rounded-lg shadow-lg w-full max-w-md p-4'>
        <div className='flex items-center justify-between mb-3'>
          <h3 className='text-sm font-semibold'>Import / Export</h3>
          <button onClick={onClose} className='text-xs px-2 py-1 rounded border bg-background hover:bg-slate-50'>{t('common.close')}</button>
        </div>
        <div className='space-y-3'>
          <p className='text-xs text-slate-600'>Wählen Sie eine Aktion:</p>
          <div className='flex gap-2'>
            <button onClick={onExport} className='flex-1 text-sm px-3 py-2 rounded border bg-background hover:bg-slate-50'>{t('common.export')}</button>
            <button onClick={onImportClick} className='flex-1 text-sm px-3 py-2 rounded border bg-background hover:bg-slate-50'>Importieren…</button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
